/**
 * useUserSettings Hook
 * 
 * Manages user settings including OpenAI API key and profile information.
 * Settings are stored in the user's Firestore document.
 */

import { useState, useEffect, useCallback } from 'react'
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../context/AuthContext'
import { USERS_COLLECTION } from '../services/users'

/**
 * Hook for managing user settings
 * 
 * @returns {object} User settings state and methods
 */
export function useUserSettings() {
  const { user } = useAuth()
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Subscribe to user settings
  useEffect(() => {
    if (!user?.uid) {
      setSettings(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const userRef = doc(db, USERS_COLLECTION, user.uid)

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setSettings({
            id: docSnap.id,
            ...docSnap.data(),
          })
        } else {
          setSettings(null)
        }
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Error fetching user settings:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [user?.uid])

  /**
   * Update user settings
   * @param {object} updates - Settings to update
   */
  const updateSettings = useCallback(async (updates) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to update settings')
    }

    setSaving(true)
    setError(null)

    try {
      const userRef = doc(db, USERS_COLLECTION, user.uid)
      await setDoc(userRef, updates, { merge: true })
    } catch (err) {
      console.error('Error updating settings:', err)
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [user?.uid])

  /**
   * Set OpenAI API key
   * @param {string} apiKey - The API key to save
   */
  const setOpenAIApiKey = useCallback(async (apiKey) => {
    await updateSettings({
      openaiApiKey: apiKey?.trim() || null,
    })
  }, [updateSettings])

  /**
   * Get OpenAI API key
   * @returns {string|null} The stored API key
   */
  const getOpenAIApiKey = useCallback(() => {
    return settings?.openaiApiKey || null
  }, [settings])

  /**
   * Check if API key is set
   * @returns {boolean} True if API key exists
   */
  const hasOpenAIApiKey = useCallback(() => {
    return !!settings?.openaiApiKey
  }, [settings])

  /**
   * Set ElevenLabs API key
   * @param {string} apiKey - The API key to save
   */
  const setElevenLabsApiKey = useCallback(async (apiKey) => {
    await updateSettings({
      elevenlabsApiKey: apiKey?.trim() || null,
    })
  }, [updateSettings])

  /**
   * Get ElevenLabs API key
   * @returns {string|null} The stored API key
   */
  const getElevenLabsApiKey = useCallback(() => {
    return settings?.elevenlabsApiKey || null
  }, [settings])

  /**
   * Check if ElevenLabs API key is set
   * @returns {boolean} True if API key exists
   */
  const hasElevenLabsApiKey = useCallback(() => {
    return !!settings?.elevenlabsApiKey
  }, [settings])

  /**
   * Update display name
   * @param {string} displayName - New display name
   */
  const setDisplayName = useCallback(async (displayName) => {
    await updateSettings({
      displayName: displayName?.trim() || null,
    })
  }, [updateSettings])

  /**
   * Update profile picture URL
   * @param {string} photoURL - New photo URL
   */
  const setPhotoURL = useCallback(async (photoURL) => {
    await updateSettings({
      photoURL: photoURL || null,
    })
  }, [updateSettings])

  /**
   * Update user title (e.g., "Project Manager", "Writer")
   * @param {string} title - New title
   */
  const setTitle = useCallback(async (title) => {
    await updateSettings({
      title: title?.trim() || null,
    })
  }, [updateSettings])

  return {
    // State
    settings,
    loading,
    error,
    saving,
    
    // Getters
    displayName: settings?.displayName || null,
    photoURL: settings?.photoURL || null,
    title: settings?.title || null,
    email: settings?.email || user?.email || null,
    
    // OpenAI API Key methods
    getOpenAIApiKey,
    setOpenAIApiKey,
    hasOpenAIApiKey,
    
    // ElevenLabs API Key methods
    getElevenLabsApiKey,
    setElevenLabsApiKey,
    hasElevenLabsApiKey,
    
    // Profile methods
    setDisplayName,
    setPhotoURL,
    setTitle,
    
    // Generic update
    updateSettings,
  }
}

export default useUserSettings
